import { api } from "@repo/convex/api";
import type { Id } from "@repo/convex/dataModel";
import { humanizeKey } from "@repo/shared";
import {
  type DismissalDto,
  type PanelScopeDto,
  type QgDismissalTag,
  type QgNotAssessedReason,
  type ScopeRef,
  scopeKey,
} from "@repo/shared/qg-next/types";
import { useQuery } from "convex/react";
import type { FunctionReturnType } from "convex/server";
import { CircleDashed, ScanLine, ShieldAlert, ShieldCheck } from "lucide-react";
import { useRef, useState } from "react";
import { toast } from "sonner";
import { AdminFormDialog } from "@/components/ui/admin-form-dialog";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Spinner } from "@/components/ui/spinner";
import { StatusPanel } from "@/components/ui/status-panel";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { useExhaustivePaginatedQuery } from "@/features/df-admin/components/claudia-evals/hooks/use-exhaustive-paginated-query";
import { useObservedMutation } from "@/hooks/use-observed-convex";
import { resolveUser, useOrgUserMap } from "@/hooks/use-org-user-map";
import { trackQualityGuardEvent } from "@/lib/analytics/domain";
import { getConvexErrorMessage } from "@/lib/convex-errors";
import { formatSmartDate } from "@/lib/formatters";
import {
  notifyQualityGuardScanStarted,
  QUALITY_GUARD_ENTITY_SCAN_STARTED_MESSAGE,
} from "@/lib/quality-guard-toasts";
import { useRightSidebarClaudia, useRightSidebarUi } from "../../contexts";
import { buildClaudiaFixScopeIntent, type ClaudiaFixScope } from "./build-claudia-fix-intent";
import { QgNextStateBadge } from "./issue-badge";
import { QgNextSuggestedMentions } from "./qg-next-suggested-mentions";
import { type SectionLabels, useSectionLabels } from "./use-section-labels";

export type QgNextEntityPanelData = Extract<
  FunctionReturnType<typeof api.qgNext.read.getEntityPanel>,
  { ofRecord: true }
>;
type PanelScope = QgNextEntityPanelData["scopes"][number];

/** The states a section can be dismissed in: it has issues of its own. */

/** `known, accepted` is not offered while the section's highest issue is High. */
export function dismissalTagsFor(state: string): QgDismissalTag[] {
  return state === "high" ? ["not_an_issue"] : ["not_an_issue", "known_accepted"];
}

/**
 * The states a person is told about in the list; green, still-checking and empty scopes are not
 * listed as issues.
 */
const LISTED_STATES = new Set(["high", "medium", "low", "not_checked"]);

const NOT_ASSESSED_WORDS: Record<QgNotAssessedReason, string> = {
  no_answer: "the check gave no answer",
  malformed: "the answer could not be read",
  oversize: "the content was too large to send",
  call_failed: "the check could not be reached",
  fact_unavailable: "a fact it needs could not be worked out",
  context_too_large: "what it reads was too large to send",
  proposal_malformed: "the elements it might name could not be read",
  pack_unavailable: "its rules were not ready to check with",
};

/** The words a dismissal's tag and status are shown in, the panel's and the project view's. */
export const QG_NEXT_DISMISSAL_TAG_LABELS: Record<DismissalDto["tag"], string> = {
  not_an_issue: "Not an issue",
  known_accepted: "Known, accepted",
};

function scopePlace(scope: ScopeRef): { tabKey: string | null; fieldId: string | null } {
  if (scope.kind === "section") return { tabKey: scope.tabKey, fieldId: scope.fieldId };
  if (scope.kind === "tab") return { tabKey: scope.tabKey, fieldId: null };
  return { tabKey: null, fieldId: null };
}

export const QG_NEXT_DISMISSAL_STATUS_LABELS: Record<DismissalDto["status"], string> = {
  current: "Current",
  superseded: "Superseded",
};

/** What a scope is called in the panel: the section, the whole tab, or the whole element. */
export function qgNextScopeLabel(scope: ScopeRef, labels: SectionLabels): string {
  if (scope.kind === "section") return labels.fieldLabel ?? humanizeKey(scope.fieldId);
  if (scope.kind === "tab") return labels.tabLabel ?? humanizeKey(scope.tabKey);
  return "Whole element";
}

function matchesSection(
  scope: ScopeRef,
  section: { tabKey: string | null; fieldId: string | null }
): boolean {
  if (!section.fieldId) return true;
  return (
    scope.kind === "section" &&
    scope.fieldId === section.fieldId &&
    (!section.tabKey || scope.tabKey === section.tabKey)
  );
}

/**
 * The one line under a summary that says how far it can be trusted, and whether a new one is
 * being written, or nothing.
 */
export function qgNextSummaryNote(
  entry: Pick<PanelScopeDto, "state" | "summaryStatus" | "summaryWriting">
): string | null {
  if (entry.state === "not_checked" || entry.summaryStatus === "outdated") {
    return entry.summaryWriting
      ? "This summary may be outdated. A new summary is being written."
      : "This summary may be outdated.";
  }
  if (entry.summaryStatus === "partial") {
    return "This section has further issues Claudia can fix.";
  }
  if (entry.summaryStatus === "failed") return "The summary could not be written.";
  return null;
}

/**
 * What a scope with no summary says in its place, in words a person reads: a summary on its way,
 * a check still running, a check that could not be completed, or a summary that could not be
 * written. Never a placeholder for text that is not coming.
 */
export function qgNextMissingSummary(
  entry: Pick<PanelScopeDto, "state" | "summaryStatus">
): string {
  if (entry.summaryStatus === "writing") return "The summary is being written.";
  if (entry.summaryStatus === "failed") return "The summary could not be written.";
  if (entry.state === "pending") return "This section is being checked.";
  if (entry.state === "not_checked") return "This section could not be checked yet.";
  return "The summary is being written.";
}

/**
 * The note under a scope's summary; none when there is no summary, whose place
 * `qgNextMissingSummary` already explains.
 */
export function qgNextShownSummaryNote(
  entry: Pick<PanelScopeDto, "state" | "summaryStatus" | "summary" | "summaryWriting">
): string | null {
  return entry.summary ? qgNextSummaryNote(entry) : null;
}

/**
 * What the shown scopes did not cover: rules not checked with the reason in words, rules still
 * being checked, and parked rules. Counts only; a person is never shown a rule id.
 */
export function coverageLines(scopes: PanelScopeDto[], parkedCount: number): string[] {
  const byReason = new Map<QgNotAssessedReason, number>();
  let pending = 0;
  for (const scope of scopes) {
    pending += scope.coverage.pending;
    for (const entry of scope.coverage.notChecked) {
      byReason.set(entry.reason, (byReason.get(entry.reason) ?? 0) + 1);
    }
  }
  const rules = (count: number) => `${count} ${count === 1 ? "rule" : "rules"}`;
  const lines = [...byReason].map(
    ([reason, count]) => `${rules(count)} not checked: ${NOT_ASSESSED_WORDS[reason]}.`
  );
  if (pending > 0) lines.push(`${rules(pending)} still being checked.`);
  if (parkedCount > 0) {
    lines.push(`${rules(parkedCount)} parked by the rule authors and not checked.`);
  }
  return lines;
}

function ScopeRow({
  entry,
  label,
  tabLabel,
  actions,
}: {
  entry: PanelScope;
  label: string;
  tabLabel?: string;
  actions?: React.ReactNode;
}) {
  const note = qgNextShownSummaryNote(entry);
  return (
    <div data-qg-next-scope={entry.scope.kind} className="border-b border-border px-4 py-3">
      <div className="flex min-w-0 items-center gap-2">
        <QgNextStateBadge label={`Quality Guard for ${label}`} state={entry.state} />
        <div className="min-w-0">
          <p className="truncate text-sm font-medium">{label}</p>
          {tabLabel ? (
            <p className="truncate text-[11px] text-muted-foreground">{tabLabel}</p>
          ) : null}
        </div>
      </div>
      <p data-qg-next-summary="true" className="mt-2 text-sm leading-relaxed">
        {entry.summary ?? (
          <span className="text-muted-foreground">{qgNextMissingSummary(entry)}</span>
        )}
      </p>
      {note ? <p className="mt-1 text-[11px] text-muted-foreground">{note}</p> : null}
      <QgNextSuggestedMentions mentions={entry.suggestedMentions} />
      {actions ? <div className="mt-2 flex flex-wrap items-center gap-2">{actions}</div> : null}
    </div>
  );
}

function DismissalRow({
  dismissal,
  label,
  userMap,
}: {
  dismissal: DismissalDto;
  label: string;
  userMap: ReturnType<typeof useOrgUserMap>;
}) {
  return (
    <div data-qg-next-dismissal={dismissal.status} className="border-b border-border px-4 py-3">
      <div className="flex items-center justify-between gap-2">
        <p className="truncate text-sm font-medium">{label}</p>
        <Badge variant={dismissal.status === "current" ? "info" : "outline"}>
          {QG_NEXT_DISMISSAL_STATUS_LABELS[dismissal.status]}
        </Badge>
      </div>
      <p className="mt-0.5 text-[11px] text-muted-foreground">
        {QG_NEXT_DISMISSAL_TAG_LABELS[dismissal.tag]} ·{" "}
        {resolveUser(userMap, dismissal.dismissedBy as Id<"users">).fullName} ·{" "}
        {formatSmartDate(dismissal.dismissedAt)}
      </p>
      <p className="mt-1.5 text-sm leading-relaxed">{dismissal.reason}</p>
      {dismissal.summaryAtTime ? (
        <p className="mt-1.5 border-l-2 border-border pl-2 text-xs leading-relaxed text-muted-foreground">
          {dismissal.summaryAtTime}
        </p>
      ) : null}
    </div>
  );
}

function DismissDialog({
  projectId,
  target,
  onClose,
}: {
  projectId: Id<"projects">;
  target: { entry: PanelScope; label: string } | null;
  onClose: () => void;
}) {
  const [reason, setReason] = useState("");
  const [tag, setTag] = useState<QgDismissalTag>("not_an_issue");
  const dismiss = useObservedMutation(api.qgNext.dismiss.dismissSection, {
    feature: "quality-guard",
    operation: "qgNextDismissSection",
    includeRequestId: true,
  });
  const tags = target ? dismissalTagsFor(target.entry.state) : [];
  const close = () => {
    setReason("");
    setTag("not_an_issue");
    onClose();
  };
  return (
    <AdminFormDialog
      open={target !== null}
      onOpenChange={(open) => {
        if (!open) close();
      }}
      title={`Dismiss ${target?.label ?? "section"} for this version`}
      description="Say why the section is right as it stands. The dismissal holds until its words, what it reads or its rules change."
      submitLabel="Dismiss for this version"
      submitDisabled={!reason.trim() || !tags.includes(tag)}
      onSubmit={async () => {
        if (!target) return;
        try {
          await dismiss({
            projectId,
            scopeKey: scopeKey(target.entry.scope),
            reason: reason.trim(),
            tag,
          });
          toast.success("Section dismissed for this version");
          close();
        } catch (error) {
          toast.error(getConvexErrorMessage(error) ?? "The section could not be dismissed");
        }
      }}
      size="sm"
    >
      <RadioGroup
        value={tag}
        onValueChange={(value) => setTag(value as QgDismissalTag)}
        aria-label="Why it is dismissed"
      >
        {tags.map((option) => (
          // biome-ignore lint/a11y/noLabelWithoutControl: the nested RadioGroupItem renders the control.
          <label key={option} className="flex items-center gap-2 text-sm">
            <RadioGroupItem value={option} />
            {QG_NEXT_DISMISSAL_TAG_LABELS[option]}
          </label>
        ))}
      </RadioGroup>
      <Textarea
        aria-label="Reason"
        value={reason}
        onChange={(event) => setReason(event.target.value)}
        placeholder="Why the section is right as it stands"
        className="min-h-24 text-sm"
      />
    </AdminFormDialog>
  );
}

/**
 * The operator's Scan of this element: every scope of it re-checked by Quality Guard next. Shown
 * only to someone who may operate Quality Guard on the project; while a scope of the element is
 * being checked it says so instead.
 */
function ElementScanButton({
  projectId,
  entityTypeKey,
  entityId,
  checking,
}: {
  projectId: Id<"projects">;
  entityTypeKey: string;
  entityId: string;
  checking: boolean;
}) {
  const operatorState = useQuery(api.qualityGuard.canOperateScope, { projectId });
  const forceRefreshScope = useObservedMutation(api.qualityGuard.forceRefreshScope, {
    feature: "quality-guard",
    operation: "forceRefreshScope",
    includeRequestId: true,
  });
  const [requested, setRequested] = useState(false);
  const inFlight = useRef(false);
  if (operatorState?.canOperate !== true) return null;
  const busy = requested || checking;
  const run = () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setRequested(true);
    trackQualityGuardEvent("quality_guard_scan_started", {
      scope_kind: "entity",
      project_id: projectId,
      entity_type_key: entityTypeKey,
      entity_id: entityId,
    });
    void forceRefreshScope({
      projectId,
      scopeKind: "entity",
      entityTypeKey,
      entityId,
      requestId: `manual-entity-scan-${entityTypeKey}-${entityId}-${Date.now()}`,
      reason: "Manual entity Quality Guard scan from UI",
    })
      .then(() => notifyQualityGuardScanStarted(QUALITY_GUARD_ENTITY_SCAN_STARTED_MESSAGE))
      .catch((error: unknown) =>
        toast.error(getConvexErrorMessage(error) ?? "The Quality Guard scan could not be started.")
      )
      .finally(() => {
        inFlight.current = false;
        setRequested(false);
      });
  };
  return (
    <Button
      type="button"
      size="sm"
      className="h-7 shrink-0 gap-1.5"
      data-testid="qg-next-element-scan"
      disabled={busy}
      onClick={run}
    >
      {busy ? (
        <Spinner data-icon="inline-start" className="size-3.5" />
      ) : (
        <ScanLine data-icon="inline-start" className="size-3.5" />
      )}
      <span>{busy ? "Checking..." : "Run scan"}</span>
    </Button>
  );
}

/** Dismissals asked per page; the server reads one plain-write page whatever is asked. */
const DISMISSAL_PAGE_SIZE = 200;

/**
 * The Quality Guard panel when Quality Guard 2 is the engine of record: for each section with
 * issues its state and the one summary written for people, what was not checked, and the
 * Dismissed tab. People are never shown findings or a count per section.
 */
export function QgNextEntityPanel({
  projectId,
  entityTypeKey,
  entityId,
  entityName,
  sectionTabKey,
  sectionFieldId,
  panel,
}: {
  projectId: Id<"projects">;
  entityTypeKey: string;
  entityId: string;
  entityName?: string | null;
  sectionTabKey?: string | null;
  sectionFieldId?: string | null;
  panel: QgNextEntityPanelData;
}) {
  const [tab, setTab] = useState<"issues" | "dismissed">("issues");
  const [dismissTarget, setDismissTarget] = useState<{ entry: PanelScope; label: string } | null>(
    null
  );
  const userMap = useOrgUserMap();
  // The element's dismissal history grows with every version, so it is read a page at a time.
  const dismissalHistory = useExhaustivePaginatedQuery(
    api.qgNext.read.listEntityDismissals,
    { projectId, entityId },
    { pageSize: DISMISSAL_PAGE_SIZE }
  );
  const { labels, position } = useSectionLabels(projectId, entityTypeKey);
  const { expandToPanel } = useRightSidebarUi();
  const { pendingAskClaudiaIntent, setAskClaudiaIntent } = useRightSidebarClaudia();
  const fixWithClaudia = (scope: ClaudiaFixScope, scopeLabel: string) => {
    const place = scopePlace(scope);
    setAskClaudiaIntent(
      buildClaudiaFixScopeIntent({
        scope,
        entityCtx: { entityTypeKey, entityId, entityName },
        labels: labels(place.tabKey, place.fieldId),
        scopeLabel,
      })
    );
    expandToPanel("claudia");
  };
  const section = { tabKey: sectionTabKey ?? null, fieldId: sectionFieldId ?? null };
  const labelOf = (scope: ScopeRef) => {
    const place = scopePlace(scope);
    return qgNextScopeLabel(scope, labels(place.tabKey, place.fieldId));
  };
  const order = (scope: ScopeRef) => {
    const place = scopePlace(scope);
    return scope.kind === "element" ? -1 : position(place.tabKey, place.fieldId);
  };

  const shown = panel.scopes
    .filter((entry) => matchesSection(entry.scope, section))
    .sort((left, right) => order(left.scope) - order(right.scope));
  const listed = shown.filter((entry) => LISTED_STATES.has(entry.state));
  // A field's view counts the parked rules that name it; the element's view counts them all.
  const parked = section.fieldId
    ? panel.parked.filter((rule) => rule.scopes.some((scope) => matchesSection(scope, section)))
    : panel.parked;
  const coverage = coverageLines(shown, parked.length);
  const dismissals = dismissalHistory.rows.filter((dismissal) =>
    matchesSection(dismissal.scope, section)
  );
  const allGreen =
    shown.length > 0 &&
    parked.length === 0 &&
    shown.every((entry) => entry.state === "green" && entry.coverage.complete);
  // Empty sections were not checked: they are listed apart, never as issues and never as green.
  const empty = shown.filter((entry) => entry.state === "empty");
  const filledClean =
    empty.length > 0 &&
    parked.length === 0 &&
    shown.every(
      (entry) => (entry.state === "green" || entry.state === "empty") && entry.coverage.complete
    );
  const title = entityName?.trim() || "Current entity";
  // Scopes queued or being checked are told apart from the element's number, never in it.
  const checking = section.fieldId
    ? shown.filter((entry) => entry.state === "pending").length
    : panel.checkingCount;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex shrink-0 items-start justify-between gap-3 border-b border-border px-4 py-3">
        <div className="min-w-0 space-y-1">
          <p className="truncate text-sm font-medium">{title}</p>
          <p className="text-xs text-muted-foreground">
            {section.fieldId
              ? `Quality Guard for ${labels(section.tabKey, section.fieldId).fieldLabel ?? humanizeKey(section.fieldId)}.`
              : "Quality Guard for the latest saved version."}
          </p>
          {checking > 0 ? (
            <p
              data-testid="qg-next-checking"
              className="inline-flex items-center gap-1 text-xs text-muted-foreground"
            >
              <Spinner className="size-3" />
              Checking <span className="tabular-nums">{checking}</span>
            </p>
          ) : null}
        </div>
        <ElementScanButton
          projectId={projectId}
          entityTypeKey={entityTypeKey}
          entityId={entityId}
          checking={panel.scopes.some((entry) => entry.state === "pending")}
        />
      </div>
      <Tabs
        value={tab}
        onValueChange={(value) => setTab(value as "issues" | "dismissed")}
        className="flex min-h-0 flex-1 flex-col gap-0"
      >
        <div className="flex shrink-0 items-center border-b border-border">
          <TabsList variant="primary" className="w-auto border-b-0">
            <TabsTrigger value="issues">Issues</TabsTrigger>
            <TabsTrigger value="dismissed">Dismissed</TabsTrigger>
          </TabsList>
        </div>
        <TabsContent value="issues" className="min-h-0 flex-1">
          <ScrollArea className="h-full">
            {coverage.length > 0 ? (
              <div className="px-4 pt-3">
                <Alert data-testid="qg-next-coverage">
                  <ShieldAlert />
                  <AlertTitle>Not everything was checked</AlertTitle>
                  <AlertDescription>
                    {coverage.map((line) => (
                      <p key={line}>{line}</p>
                    ))}
                  </AlertDescription>
                </Alert>
              </div>
            ) : null}
            {listed.length > 0 ? (
              <section data-testid="qg-next-sections" className="pb-4">
                {listed.map((entry) => {
                  const label = labelOf(entry.scope);
                  const place = scopePlace(entry.scope);
                  const tabLabel =
                    entry.scope.kind === "section"
                      ? labels(place.tabKey, null).tabLabel
                      : undefined;
                  return (
                    <ScopeRow
                      key={`${entry.scope.kind}:${place.tabKey}:${place.fieldId}`}
                      entry={entry}
                      label={label}
                      {...(tabLabel ? { tabLabel } : {})}
                      actions={
                        entry.scope.kind === "section" ||
                        entry.scope.kind === "tab" ||
                        entry.scope.kind === "element" ? (
                          <>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="h-7 text-xs"
                              disabled={Boolean(pendingAskClaudiaIntent)}
                              onClick={() => fixWithClaudia(entry.scope as ClaudiaFixScope, label)}
                            >
                              Fix with Claudia
                            </Button>
                            {entry.dismissable ? (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="h-7 text-xs"
                                onClick={() => setDismissTarget({ entry, label })}
                              >
                                Dismiss for this version
                              </Button>
                            ) : null}
                          </>
                        ) : null
                      }
                    />
                  );
                })}
              </section>
            ) : allGreen ? (
              <StatusPanel
                data-testid="qg-next-all-green"
                size="compact"
                tone="success"
                icon={ShieldCheck}
                title="Everything checks out"
                description="Every rule that applies here passed."
              />
            ) : filledClean ? (
              <StatusPanel
                data-testid="qg-next-filled-clean"
                size="compact"
                icon={CircleDashed}
                title="Nothing to fix in the filled sections"
                description={`${empty.length} ${empty.length === 1 ? "section is" : "sections are"} empty and could not be checked.`}
              />
            ) : coverage.length === 0 && empty.length === 0 ? (
              <StatusPanel
                size="compact"
                icon={CircleDashed}
                title="Not checked yet"
                description="Quality Guard has not checked this version yet."
              />
            ) : null}
            {empty.length > 0 ? (
              <section data-testid="qg-next-empty-sections" className="px-4 py-3">
                <p className="text-xs font-medium text-muted-foreground">Empty sections</p>
                <ul className="mt-1.5 space-y-1">
                  {empty.map((entry) => {
                    const place = scopePlace(entry.scope);
                    const label = labelOf(entry.scope);
                    return (
                      <li
                        key={`${entry.scope.kind}:${place.tabKey}:${place.fieldId}`}
                        data-qg-next-empty-scope="true"
                        className="flex items-center justify-between gap-2 text-sm"
                      >
                        <span className="truncate">{label}</span>
                      </li>
                    );
                  })}
                </ul>
              </section>
            ) : null}
          </ScrollArea>
        </TabsContent>
        <TabsContent value="dismissed" className="min-h-0 flex-1">
          <ScrollArea className="h-full">
            {dismissals.length > 0 ? (
              <section data-testid="qg-next-dismissals">
                {dismissals.map((dismissal) => (
                  <DismissalRow
                    key={dismissal.dismissalId}
                    dismissal={dismissal}
                    label={labelOf(dismissal.scope)}
                    userMap={userMap}
                  />
                ))}
              </section>
            ) : dismissalHistory.status !== "complete" ? (
              <div className="flex justify-center py-8">
                <Spinner className="size-4" />
              </div>
            ) : (
              <StatusPanel
                size="compact"
                icon={CircleDashed}
                title="Nothing dismissed"
                description="Sections dismissed for their current version are listed here."
              />
            )}
          </ScrollArea>
        </TabsContent>
      </Tabs>
      <DismissDialog
        projectId={projectId}
        target={dismissTarget}
        onClose={() => setDismissTarget(null)}
      />
    </div>
  );
}
